import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catalogosApi } from "@/features/catalogos/services/catalogos.api";
import { qk } from "@/lib/query-keys";

/**
 * El catálogo de claves producto/servicio del SAT tiene ~52 mil claves · ya no se descarga completo.
 * Dos formas de consultarlo:
 *  · `useBuscarClavesSat` · búsqueda en el servidor para los selectores (clave, descripción o palabras similares).
 *  · `useClavesSatConsultadas` · descripción y vigencia SOLO de las claves de unas líneas (prorrateo).
 * Ambas cuelgan de `qk.clavesSat`, así que invalidar esa llave (p. ej. al actualizar el catálogo) las refresca.
 */

const TAMANO_PAGINA_PICKER = 20;

/** Claves UTILIZABLES hoy que coinciden con la búsqueda (primera página). */
export function useBuscarClavesSat(busqueda: string, habilitado = true) {
  const texto = busqueda.trim();
  return useQuery({
    queryKey: [...qk.clavesSat, "buscar", texto],
    queryFn: () => catalogosApi.buscarClavesSat({ busqueda: texto || undefined, page: 1, pageSize: TAMANO_PAGINA_PICKER }),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * Descripción oficial y vigencia de las claves de unas líneas del prorrateo.
 *  · `descripcionDe(clave)` · descripción del catálogo (undefined si la clave no existe en él).
 *  · `catalogadas` · claves que existen y hoy son utilizables (D-9). Mientras carga se consideran TODAS catalogadas
 *    para no mostrar un bloqueo falso que parpadea; el backend valida igual al confirmar.
 */
export function useClavesSatConsultadas(claves: ReadonlyArray<string | null | undefined>) {
  const unicas = useMemo(
    () => Array.from(new Set(claves.map((c) => c?.trim() ?? "").filter((c) => c.length > 0))).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [claves.join("|")]
  );
  const { data } = useQuery({
    queryKey: [...qk.clavesSat, "consultar", unicas],
    queryFn: () => catalogosApi.consultarClavesSat(unicas),
    enabled: unicas.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  return useMemo(() => {
    const porClave = new Map((data ?? []).map((c) => [c.clave, c]));
    const cargando = unicas.length > 0 && data === undefined;
    const catalogadas: ReadonlySet<string> = cargando
      ? new Set(unicas)
      : new Set((data ?? []).filter((c) => c.catalogada !== false).map((c) => c.clave.trim()));
    return {
      cargando,
      catalogadas,
      descripcionDe: (clave: string | null | undefined) => (clave ? porClave.get(clave.trim())?.descripcion : undefined),
    };
  }, [data, unicas]);
}
